import React, { memo, useCallback, useEffect, useState, useMemo } from 'react'
import { NextPage } from 'next'
import {
  Box,
  TextInput,
  Flex,
  InputGroup,
  InputRightElement,
  Icon,
  GoogleMaps,
  Marker,
  MarkerClusterer,
  useStoreSearch,
  useStoresQuery,
  Spinner,
  Text,
  Title,
  Container,
  Button,
  OverlayView,
  Card,
  Heading,
  Stack,
  LinkButton,
  Grid,
  Link,
  AddressInput,
} from '@thirstycamel/ui'
import type { StoreEntity } from '@ts/core/src/modules/store/store.entity'

import LayoutMain from '../layout/LayoutMain'
import StoreCard from '../components/StoreCard'
import { config } from '../utils/config'
import { mapLatLngByRegion, mapZoomByRegion } from '../utils/map-locations'
import { useStore } from '../store/hooks'
import { useRouter } from 'next/router'
import TownInput from '@thirstycamel/ui/lib/components/AddressInput/TownInput'

const AUSTRALIA_ZOOM = 4
const AUSTRALIA_LAT_LNG = { lat: -27.8174082, lng: 135.9093002 }

const MAX_RENDER_NUMBER = 10 // Max number of items to be rendered in search results grid

enum tabs {
  LIST,
  MAP,
}

const StoreLocator: NextPage = () => {
  const router = useRouter()

  const selectedStore = useStore(s => s.store.selectedStore)
  const location = useStore(store => store.location)
  const [locationHint, setLocationHint] = useState<{ lat: number; lng: number }>(null)

  const [map, setMap] = useState<google.maps.Map>(null)
  const [selectedMarker, setSelectedMarker] = useState<StoreEntity>(null)
  const [tab, setTab] = useState(tabs.MAP)

  const defaultSearchValue = (router.query.q as string) || ''

  const search = useStoreSearch<StoreEntity>({
    debounce: 700,
    defaultSearchValue,
    lat: locationHint?.lat,
    lng: locationHint?.lng,
  })
  const stores = useStoresQuery<StoreEntity[]>({
    lat: selectedStore?.point?.coordinates[1],
    lng: selectedStore?.point?.coordinates[0],
  })

  const results = search.resolvedData?.items || stores.data
  const error = search.error || stores.error

  const defaultZoom = selectedStore ? 13 : mapZoomByRegion[location.region] || AUSTRALIA_ZOOM
  const defaultLatLng = useMemo(
    () =>
      selectedStore && selectedStore.point
        ? { lat: selectedStore.point.coordinates[1], lng: selectedStore.point.coordinates[0] }
        : mapLatLngByRegion[location.region] || AUSTRALIA_LAT_LNG,
    [selectedStore?.id],
  )

  // console.log(defaultLatLng)

  useEffect(() => {
    /* Send to Facebook Pixel */
    if (typeof fbq !== 'undefined') {
      fbq?.('track', 'FindLocation')
    }
  }, [search.searchValue])

  useEffect(() => {
    if (!map) return

    if (selectedMarker) setSelectedMarker(null)

    if (!search.resolvedData?.items?.length) {
      /* If there are no results, zoom back out to default. */
      map.setZoom(defaultZoom)
      map.setCenter(defaultLatLng)
      return
    }

    const _bounds = new google.maps.LatLngBounds()

    search.resolvedData.items.forEach(({ point }) => {
      const [lng, lat] = point?.coordinates || []

      const position = new google.maps.LatLng(lat, lng)
      const marker = new google.maps.Marker({ position })

      _bounds.extend(marker.getPosition())
    })

    setTimeout(() => {
      map.fitBounds(_bounds)

      /* Fix zooming in too far if only 1 result */
      const zoom = map.getZoom()
      if (zoom > 15) map.setZoom(15)
    }, 200)
  }, [search.resolvedData])

  const handleMapsLoaded = mapInstance => {
    setMap(mapInstance)
  }

  const handleSubmit = event => {
    event.preventDefault()

    search.refetch()
  }

  const renderClusterer = useCallback(
    clusterer => {
      if (!results) return []

      return results.map(store => {
        const [lng, lat] =
          (Array.isArray(store.point?.coordinates) && store.point?.coordinates) || []

        if (!lat || !lng) return null

        return (
          <Marker
            key={store.id}
            position={{ lng, lat }}
            clusterer={clusterer}
            animation={google.maps.Animation.DROP}
            onClick={() => setSelectedMarker(store)}
          />
        )
      })
    },
    [results],
  )

  const _results = useMemo(
    () =>
      results
        ?.filter(r =>
          search.searchValue || locationHint
            ? true
            : r.region === (selectedStore ? selectedStore.region : location.region),
        )
        .slice(0, MAX_RENDER_NUMBER),
    [results, selectedStore, location.region],
  )

  return (
    <LayoutMain title="Bottleshop Finder" noContainer>
      <Flex
        direction={['column', , 'row']}
        justify="center"
        align="center"
        py={[6, , 8]}
        px={4}
        bg="gray.50"
      >
        <Title fontSize="lg" mr={[0, , 8]} mb={[4, , 0]}>
          Find a Bottleshop
        </Title>

        <Box as="form" onSubmit={handleSubmit} w="100%" maxWidth="300px">
          <TownInput
            width="100%"
            onChangeValue={value => {
              if (typeof value !== 'string' && value && value.lat && value.lng) {
                setLocationHint({ lat: value.lat, lng: value.lng })
              } else {
                setLocationHint(null)
              }
            }}
            onChangeInputValue={value => {
              if (new RegExp('^\\d{4}$').test(value)) {
                search.setSearchValue(value)
              } else {
                search.setSearchValue('')
              }
            }}
          />
        </Box>
      </Flex>

      <Flex
        display={['', 'none']}
        position="sticky"
        top="112px"
        zIndex={3}
        borderBottom="1px"
        borderColor="gray.200"
      >
        <Button
          variant="ghost"
          w="50%"
          variantColor={tab === tabs.MAP ? 'pink' : 'black'}
          bg={tab === tabs.MAP ? 'white' : 'gray.50'}
          borderColor="pink.500"
          borderTop={tab === tabs.MAP ? '2px' : 0}
          borderRight={tab === tabs.MAP ? '2px' : 0}
          onClick={() => setTab(tabs.MAP)}
          _focus={{ borderColor: 'pink.500', outline: 0 }}
        >
          VIEW MAP
        </Button>

        <Button
          variant="ghost"
          w="50%"
          variantColor={tab === tabs.LIST ? 'pink' : 'black'}
          bg={tab === tabs.LIST ? 'white' : 'gray.50'}
          borderColor="pink.500"
          borderTop={tab === tabs.LIST ? '2px' : 0}
          borderLeft={tab === tabs.LIST ? '2px' : 0}
          onClick={() => setTab(tabs.LIST)}
          _focus={{ borderColor: 'pink.500', outline: 0 }}
        >
          VIEW AS LIST
        </Button>
      </Flex>

      <Box
        h={[tab === tabs.MAP ? '60vh' : 0, '30vh']}
        maxHeight={[, '500px']}
        minHeight={[, '500px']}
        w="100%"
        bg="gray.50"
        display={[tab === tabs.LIST ? 'none' : null, 'block']}
      >
        <GoogleMaps
          zoom={defaultZoom}
          center={defaultLatLng}
          onLoad={handleMapsLoaded}
          onDragEnd={() => setSelectedMarker(null)}
        >
          <MarkerClusterer>{renderClusterer}</MarkerClusterer>

          {selectedMarker && (
            <OverlayView
              position={{
                lat: selectedMarker.point?.coordinates?.[1],
                lng: selectedMarker.point?.coordinates?.[0],
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Stack
                as={Card}
                spacing={0}
                boxShadow="lg"
                maxWidth="20rem"
                pos="relative"
                bottom="-20px"
                transform="translateX(-50%)"
                border={0}
              >
                {/* This triangle is placed under the lighter triangle to create a 'border' effect around it. */}
                <Box
                  pos="absolute"
                  bottom="100%"
                  left="50%"
                  transform="translateX(-50%)"
                  w={0}
                  h={0}
                  borderLeft="15px solid transparent"
                  borderRight="15px solid transparent"
                  borderBottom="15px solid #b2d985"
                />

                {/* This is the triangle above the card (to indicate it points to the marker). */}
                <Box
                  pos="absolute"
                  bottom="calc(100% - 1px)"
                  left="50%"
                  transform="translateX(-50%)"
                  w={0}
                  h={0}
                  borderLeft="15px solid transparent"
                  borderRight="15px solid transparent"
                  borderBottom="15px solid #ecf5e0"
                />

                <Stack
                  isInline
                  // align="center"
                  justify="space-between"
                  spacing={4}
                  bg="green.100"
                  px={4}
                  py={3}
                  border="1px"
                  borderColor="green.400"
                  borderTopLeftRadius="lg"
                  borderTopRightRadius="lg"
                >
                  <Link href="/stores/[slug]" hrefAs={`/stores/${selectedMarker.slug}`}>
                    <Heading color="pink.500" fontSize="xs" textTransform="uppercase">
                      {selectedMarker.name}
                    </Heading>
                  </Link>

                  {selectedMarker.distance && (
                    <Stack isInline spacing={1} align="center" color="pink.500" h="100%" pt="2px">
                      <Icon name="place" />
                      <Text fontSize="sm" fontWeight="bold">
                        {selectedMarker.distance}km
                      </Text>
                    </Stack>
                  )}
                </Stack>

                <Stack
                  isInline
                  align="center"
                  justify="space-between"
                  pl={4}
                  pr={1}
                  py={2}
                  border="1px"
                  borderColor="gray.200"
                  borderTop={0}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedMarker.address}
                  </Text>
                  <LinkButton
                    href="/stores/[slug]"
                    hrefAs={`/stores/${selectedMarker.slug}`}
                    variant="ghost"
                    variantColor="pink"
                    size="sm"
                  >
                    More
                  </LinkButton>
                </Stack>
              </Stack>
            </OverlayView>
          )}
        </GoogleMaps>
      </Box>

      {/* <Flex
            direction={['column', , 'row']}
            wrap="wrap"
            align={['center', , 'flex-start']}
            justify={['center', , 'flex-start']}
            w="100%"
          >
            {_results
              .filter(r => r.region === (selectedStore ? selectedStore.region : location.region))
              .map((store, index) => (
                <Box
                  key={store.name}
                  flexBasis={['100%', , '50%']}
                  w={['100%', , '50%']}
                  maxWidth="450px"
                  pl={index % 2 !== 0 ? [, , 2, 4] : null}
                  pr={index % 2 === 0 ? [, , 2, 4] : null}
                  pb={8}
                >
                  <StoreCard store={store} />
                </Box>
              ))}
          </Flex> */}

      <Container size="xs" py={[6, , 10]} display={[tab === tabs.MAP ? 'none' : null, 'flex']}>
        {_results?.length > 0 ? (
          <Grid templateColumns={['repeat(1, 1fr)', , 'repeat(2, 1fr)']} gap={8} w="100%">
            {_results
              .filter(r =>
                search.searchValue || locationHint
                  ? true
                  : r.region === (selectedStore ? selectedStore.region : location.region),
              )
              .map((store, index) => (
                <Box key={store.name} h="100%" maxWidth="450px" w="100%" mx="auto">
                  <StoreCard store={store} h="100%" />
                </Box>
              ))}
          </Grid>
        ) : error ? (
          <Text textAlign="center" mx="auto">
            Sorry, an error has occurred.
          </Text>
        ) : (
          <Stack textAlign="center" align="center" mx="auto">
            <Heading>Sorry, no results!</Heading>
            <Text>
              No stores found{search.searchValue ? ` matching '${search.searchValue}'` : ''}.
            </Text>
          </Stack>
        )}
      </Container>
    </LayoutMain>
  )
}

export default memo(StoreLocator)
